import React, { useEffect, useState } from "react";
import { NavLink, useParams } from "react-router-dom";
import { APIProvider, Map } from "@vis.gl/react-google-maps";
import { Card, Button, Modal, Form, message, Space, Spin, Alert } from "antd";
import {
  PlusOutlined,
  ArrowLeftOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { DrawingManager } from "../../components/DrawingManager";
import { LineTable } from "./components/LineTable";
import { LineForm } from "./components/LineForm";
import { useLineManagement } from "./hooks/useLineManagement";
import { useStreetDetails } from "../../hooks/useStreetDetails";
import { ExistingPolylines } from "../../components/ExistingPolylines";
import { getTypeLines } from "../../api/typeLine/typeLineRequest";
import PermissionRender from "../../components/PermissionRender";

const LinePage = () => {
  const { streetId } = useParams();
  const [form] = Form.useForm();
  const [typeLine, setTypeLine] = useState([]);

  // Fetch zone details
  const {
    streetDetails,
    loading: streetLoading,
    error: streetError,
  } = useStreetDetails(streetId);

  // Section management hook
  const {
    entities,
    currentEntity,
    isModalVisible,
    loading,
    drawingMode,
    editingEntity,
    isEditing,
    zoomToEntity,
    fetchEntities,
    handlePolygonComplete,
    startEditingEntity,
    handlePolygonEdit,
    handleViewEntity,
    cancelEditing,
    handleSave,
    handleModalClose,
    deleteEntity,
    setDrawingMode,
    setIsModalVisible,
    setParentEntity,
  } = useLineManagement(streetId, form);

  useEffect(() => {
    if (streetId) {
      fetchEntities();
    }
  }, [streetId]);

  useEffect(() => {
    if (streetDetails) {
      handleViewEntity(streetDetails);
      setParentEntity(streetDetails);
    }
  }, [streetDetails]);

  useEffect(() => {
    fetchTypeLine();
  }, []);

  const fetchTypeLine = async () => {
    try {
      const res = await getTypeLines();
      setTypeLine(res);
    } catch (err) {
      message.error("Échec du chargement des type de ligne");
    }
  };

  // Handle section deletion
  const handleDelete = (streetId) => {
    Modal.confirm({
      title: "Supprimer la Ligne",
      content:
        "Êtes-vous sûr de vouloir supprimer cette ligne ? Cette action est irréversible.",
      okText: "Oui",
      okType: "danger",
      cancelText: "Non",
      onOk: async () => {
        try {
          await deleteEntity(streetId);
          message.success("Ligne supprimée avec succès");
          fetchEntities();
        } catch (error) {
          message.error("Échec de la suppression de la ligne");
        }
      },
    });
  };

  const openEditModal = () => {
    if (editingEntity) {
      form.setFieldsValue({
        name: editingEntity.name,
        description: editingEntity.description,
        line_details: editingEntity.line_details,
      });
      setIsModalVisible(true);
    }
  };

  if (streetLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 50 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (streetError) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          message="Error"
          description="Échec du chargement des détails de la rue"
          type="error"
          showIcon
        />
      </div>
    );
  }
  return (
    <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY} region="MA">
      <div style={{ padding: 24 }}>
        {/* Header with navigation */}
        <div style={{ marginBottom: 16 }}>
          <Space>
            <NavLink to={`/sections/${streetDetails?.section_id}/streets`}>
              <Button className="ml-2" icon={<ArrowLeftOutlined />} />
            </NavLink>
            <h1 style={{ margin: 0 }}>
              Lignes pour la Rue : {streetDetails?.name}
              <Button
                className="ml-2"
                icon={<EyeOutlined />}
                onClick={() => handleViewEntity(streetDetails)}
                disabled={isEditing}
              ></Button>
            </h1>
          </Space>
        </div>

        {/* Main content */}
        <Card
          title="Gestion des Lignes"
          extra={
            <Space>
              <PermissionRender requiredPermission={["lines", "create"]}>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setDrawingMode(true)}
                  disabled={isEditing}
                >
                  Dessiner une nouvelle Ligne
                </Button>
              </PermissionRender>
            </Space>
          }
        >
          {/* Map container */}
          <div style={{ height: "50vh", marginBottom: 24 }}>
            <Map
              disableDefaultUI={false}
              options={{
                mapTypeControl: true,
                zoomControl: true,
                streetViewControl: false,
              }}
              defaultCenter={{ lat: 35.688469, lng: -5.688669 }}
              defaultZoom={11}
              gestureHandling="greedy"
              styles={[
                {
                  featureType: "poi",
                  elementType: "labels",
                  stylers: [{ visibility: "off" }],
                },
              ]}
            >
              {/* Existing polygons */}

              {streetDetails && (
                <ExistingPolylines
                  zones={entities}
                  editingZone={editingEntity}
                  onPolygonEdit={handlePolygonEdit}
                  zoomToZone={zoomToEntity}
                  isEditing={true}
                  parentBoundaries={streetDetails.coordinates}
                />
              )}

              {/* Drawing manager */}
              {drawingMode && (
                <DrawingManager
                  onPolygonComplete={handlePolygonComplete}
                  Type="POLYLINE"
                />
              )}
            </Map>
          </div>

          {/* Sections table */}
          <LineTable
            sections={entities}
            loading={loading}
            editingEntity={editingEntity}
            isEditing={isEditing}
            onView={handleViewEntity}
            onEdit={startEditingEntity}
            onDelete={handleDelete}
            onSave={openEditModal}
            onCancel={cancelEditing}
          />
        </Card>

        {/* Create/Edit modal */}
        <Modal
          title={
            editingEntity
              ? "Enregistrer les modifications de la Ligne"
              : "Créer une nouvelle Ligne"
          }
          open={isModalVisible}
          onOk={form.submit}
          onCancel={handleModalClose}
          width={600}
        >
          <LineForm form={form} onFinish={handleSave} typeLines={typeLine} />
        </Modal>
      </div>
    </APIProvider>
  );
};

export default LinePage;
